import { groupedAssetTimingRules } from 'js/src/agent/skills/assetTimingGuidance';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const textVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.textVideoBasics,
    description: 'Load before creating or editing text scene groups (scenes with no narration).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Text Video Basics

For scenes in a text-only video (or text scene groups mixed into a larger video),
where on-screen text carries the whole message. For writing the copy itself — length
budgeting, structure, and style — load \`${SKILL_NAMES.writeTextVideoScript}\`.

## One thought per scene
With no narration the video behaves like a slideshow: the viewer must be able to
take in each scene's complete thought from the screen alone. Give each thought its
own scene and don't stack several fragments that don't read as one sentence. You tend
to create overly complex scenes (eyebrow + headline + subtitle) which isn't great. Most scenes
should contain a short-sentence worth of content and should read continuously without eyebrows/subtitles.
That's fine for the occasional title scene but reads like a presentation slide.

**A long sentence is more than one scene.** One thought per scene is a floor, not
a ceiling: a sentence needing more than about four seconds to read (roughly ten
words) is too much for a single scene, however complete a thought it is. Split it
across scenes or subscenes at a natural break point. This holds even when the user
gave you an exact script to use verbatim — you may not reword it, but how it is
distributed across scenes is always yours to choose, and splitting preserves the
wording while cramming does not: an overfull scene gets silently clamped to the 7s
cap and the viewer never finishes reading it.

## Media scenes first
Prefer a media scene whenever relevant, visually coherent media exists; fall back
to a text-only design when nothing suitable turns up and the text is strong enough
to carry the scene on its own.

## Subscenes: the pacing tool
A subscene is a step within one scene: the visuals (media, background, layout) stay
on screen while one set of text animates out and the next in. It's how a longer
thought splits into short, snappy steps without jump-cutting the visuals.

Two ways to create them:
- \`${TOOL_NAMES.addSubscene}\`, then \`${TOOL_NAMES.setText}\` targeting the new step via \`subscene_index\`. The
  new subscene starts as a copy of the last one, so its text must be updated.
- Or in one call: \`${TOOL_NAMES.setText}\` with \`||\` separating the chunks — it creates the
  subscenes and distributes the text (and any \`**highlights**\`) in one step.

Split at natural break points only — a comma, a conjunction, a clause boundary
— never mid-phrase. A comma between list items is a break point only when the
items are long enough that keeping them together would overfill the scene; a
short, terse list stays on one scene.

## Durations
- The scene duration is automatically set by the system to a value where the user can read
  all of the text. (\`${TOOL_NAMES.setSceneDuration}\` with mode \`"auto"\`). Use \`"manual"\` only for
  a deliberate hold or to speed up/slowdown.
- \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` only catches scenes too *short* to read.
  Keeping scenes from running long is on you. Auto durations run from a 3.4s minimum
  to a 7s cap, so a scene near the top of that range usually means too much text:
  move some of it to another scene.
- Hit a duration target with **more scenes and more content**, never by stretching
  scene durations past their reading time. Overly long scenes drag!

## Revealing text within a scene
With no narration there is nothing to sync to, so text appears with its scene by default —
leave it that way. A deliberate reveal (a punchline after its setup, the answer after the
question, list items landing one by one) is the exception: pass \`start_time_ms\` (milliseconds
into the scene) to \`${TOOL_NAMES.setText}\`, and the asset appears then and stays to the end of the
scene. \`start_words\` doesn't apply here — there is no transcript. Before reaching for a reveal,
ask whether a subscene is the better tool: a reveal layers text onto one held visual, a
subscene swaps the text out. Never time a scene's first or only text asset.

Keep every reveal inside the scene's duration: the scene doesn't grow to fit it.

${groupedAssetTimingRules('start_time_ms')}

## When the copy doesn't fit the design
If a scene's copy only fits its design as disconnected fragments, the design is wrong
for that content — switch designs rather than degrading the words.`;
    },
};
